// « API » tab of /commerce/orders: the account's order API keys (uo_…, one
// per system that pushes or reads orders; a revoked key stops at once, so
// revoking asks first) and the integration guide with copyable examples.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Code2, Copy, KeyRound, Plus, Trash2 } from 'lucide-react';
import { copyText, del, fail, formatWhen, get, post, publicBase } from './api';
import {
    Badge, Banner, Card, ConfirmDialog, DescriptionList, EmptyState, OverflowMenu,
} from '../../design/ui';

export const CodeBlock = ({ code, label }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    return (
        <div className="co-v2__code">
            <div className="co-v2__code-head">
                <span>{label}</span>
                <button type="button" className="u-btn u-btn--ghost u-btn--sm"
                    onClick={async () => { setCopied(await copyText(code)); setTimeout(() => setCopied(false), 1500); }}>
                    {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                    {copied ? t('commerceOrders.copied', 'Copié') : t('commerceOrders.copy', 'Copier')}
                </button>
            </div>
            <pre><code>{code}</code></pre>
        </div>
    );
};

// A secret shown once (new key, new webhook secret): copy it, then dismiss.
export const SecretReveal = ({ title, value, hint, onDone }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    return (
        <Banner
            tone="success"
            title={title}
            actions={(
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={onDone}>
                    {t('commerceOrders.secretSaved', 'Je l’ai enregistré')}
                </button>
            )}
        >
            <div className="co-v2__secret">
                <code>{value}</code>
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={async () => setCopied(await copyText(value))}>
                    {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                    {copied ? t('commerceOrders.copied', 'Copié') : t('commerceOrders.copy', 'Copier')}
                </button>
            </div>
            <p className="co-v2__secret-hint">{hint}</p>
        </Banner>
    );
};

const pushExample = (base) => `curl -X POST ${base}/orders/batch \\
  -H "X-API-Key: uo_…" -H "Content-Type: application/json" \\
  -d '{
  "orders": [{
    "external_id": "SO-1001",
    "number": "#1001",
    "status": "shipped",
    "client_ref": "MA-MARQUE",
    "placed_at": "2026-10-05T09:12:00Z",
    "currency": "EUR",
    "total": 49.90,
    "customer": {"name": "Ada Lovelace", "email": "ada@example.com", "phone": "+33600000000"},
    "shipping_address": {"street": "1 rue de la Paix", "postcode": "75002", "city": "Paris", "country": "FR"},
    "ship_from": {"company": "Entrepôt Sud", "postcode": "13127", "city": "Vitrolles", "country": "FR"},
    "items": [{"sku": "MUG-01", "name": "Mug", "quantity": 2, "unit_price": 12.50}],
    "shipments": [{
      "tracking_number": "6A12345678901",
      "carrier": "Colissimo",
      "service": "Expert F",
      "shipped_at": "2026-10-05T16:40:00Z",
      "insurance": false,
      "weight_kg": 1.2,
      "length_cm": 30, "width_cm": 20, "height_cm": 10,
      "content": "Mugs en céramique",
      "product_category": "maison",
      "delivery_mode": "relay",
      "relay_point": {"id": "FR-012345", "name": "Tabac de la Gare", "postcode": "75012", "city": "Paris", "country": "FR"}
    }]
  }]
}'`;

const shipmentExample = (base) => `curl -X POST "${base}/orders/4501/shipments?source=shopify" \\
  -H "X-API-Key: uo_…" -H "Content-Type: application/json" \\
  -d '{"tracking_number": "6A12345678901", "carrier": "Colissimo", "notify_customer": true}'`;

const pullExample = (base, t) => `# ${t('commerceOrders.pullFirst', '1er appel : depuis une date')}
curl "${base}/orders?updated_since=2026-10-01T00:00:00Z&limit=500" -H "X-API-Key: uo_…"
# ${t('commerceOrders.pullNext', 'Appels suivants : reprenez au curseur renvoyé')}
curl "${base}/orders?cursor=<next_cursor>&limit=500" -H "X-API-Key: uo_…"
# ${t('commerceOrders.pullProblems', 'Seulement les commandes dont un colis a un problème en cours')}
curl "${base}/orders?problem=true" -H "X-API-Key: uo_…"`;

const ApiTab = ({ isAdmin, monitoring }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const [keys, setKeys] = useState(null);
    const [name, setName] = useState('');
    const [created, setCreated] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [confirmKey, setConfirmKey] = useState(null);
    const [revoking, setRevoking] = useState(false);
    const base = publicBase();

    const load = useCallback(async () => {
        try {
            const res = await get('/api-keys');
            setKeys(res.data.items || []);
        } catch (err) {
            setError(fail(err, t('commerceOrders.keysError', 'Impossible de charger les clés.')));
            setKeys([]);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const res = await post('/api-keys', { name: name.trim() });
            setCreated(res.data);
            setName('');
            load();
        } catch (err) {
            setError(fail(err, t('commerceOrders.keyCreateError', 'Impossible de créer la clé.')));
        } finally {
            setBusy(false);
        }
    };

    const revoke = async () => {
        setError('');
        setRevoking(true);
        try {
            await del(`/api-keys/${confirmKey.id}`);
            setConfirmKey(null);
            load();
        } catch (err) {
            setError(fail(err, t('commerceOrders.keyRevokeError', 'Impossible de révoquer la clé.')));
            setConfirmKey(null);
        } finally {
            setRevoking(false);
        }
    };

    const neverUsed = (keys || []).filter((k) => !k.last_used_at).length;

    return (
        <div className="co-v2__stack">
            <Card
                icon={KeyRound}
                title={t('commerceOrders.keysTitle', 'Clés API')}
                badge={keys ? <Badge tone={keys.length ? 'accent' : undefined}>{keys.length}</Badge> : null}
                description={t('commerceOrders.keysIntro', 'Une clé par système qui envoie ou lit vos commandes (ERP, WMS, OMS). Elle ne donne accès qu’aux commandes de ce compte.')}
                flush
                footerNote={isAdmin ? null : t('commerceOrders.adminOnlyKeys', 'Seul un administrateur peut créer ou révoquer des clés.')}
                footer={isAdmin ? (
                    <form className="co-v2__inline-form" onSubmit={create}>
                        <input className="u-input" type="text" maxLength={64} value={name} onChange={(e) => setName(e.target.value)}
                            placeholder={t('commerceOrders.keyNamePlaceholder', 'Nom de la clé (ex. ERP production)')}
                            aria-label={t('commerceOrders.keyName', 'Nom')} />
                        <button type="submit" className="u-btn u-btn--primary" disabled={busy || !name.trim()}>
                            <Plus aria-hidden="true" />
                            {t('commerceOrders.createKey', 'Créer une clé')}
                        </button>
                    </form>
                ) : null}
            >
                {(error || created) && (
                    <div className="co-v2__pad co-v2__stack-sm">
                        {error && <Banner tone="danger">{error}</Banner>}
                        {created && (
                            <SecretReveal title={t('commerceOrders.keyCreated', 'Clé « {{name}} » créée', { name: created.name })} value={created.api_key}
                                hint={t('commerceOrders.keyOnce', 'Copiez-la maintenant : elle ne sera plus jamais affichée. En cas de perte, révoquez-la et créez-en une autre.')}
                                onDone={() => setCreated(null)} />
                        )}
                    </div>
                )}
                {keys === null && <div className="co-v2__skeleton" aria-hidden="true"><span className="u-skeleton" /><span className="u-skeleton" /></div>}
                {keys && keys.length === 0 && (
                    <EmptyState icon={KeyRound} title={t('commerceOrders.noKey', 'Aucune clé pour l’instant.')}>
                        {t('ordersMoneyUi.api.noKeyHint', 'Créez une clé pour que votre ERP, WMS ou OMS envoie ses commandes et lise l’état des colis.')}
                    </EmptyState>
                )}
                {keys && keys.length > 0 && (
                    <ul className="u-list co-v2__keys">
                        {keys.map((k) => (
                            <li className="u-row" key={k.id}>
                                <span className="u-icon-chip u-icon-chip--sm u-icon-chip--neutral" aria-hidden="true"><KeyRound strokeWidth={1.8} /></span>
                                <div className="u-row__main">
                                    <div className="u-row__title">
                                        {k.name}
                                        <span className="u-code">{k.key_prefix}…</span>
                                    </div>
                                    <div className="u-row__meta">
                                        {t('ordersMoneyUi.api.keyMeta', 'Créée le {{created}} · dernier usage : {{used}}', {
                                            created: formatWhen(k.created_at, lang),
                                            used: k.last_used_at ? formatWhen(k.last_used_at, lang) : t('commerceOrders.never', 'jamais'),
                                        })}
                                    </div>
                                </div>
                                <div className="u-row__side">
                                    {!k.last_used_at && <Badge>{t('ordersMoneyUi.api.neverUsed', 'Jamais utilisée')}</Badge>}
                                    {isAdmin && (
                                        <OverflowMenu
                                            label={t('ordersMoneyUi.moreActions', 'Plus d’actions')}
                                            items={[{ label: t('commerceOrders.revoke', 'Révoquer'), icon: Trash2, danger: true, onClick: () => setConfirmKey(k) }]}
                                        />
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
                {keys && keys.length > 0 && neverUsed > 0 && (
                    <p className="co-v2__card-note">
                        {t('ordersMoneyUi.api.neverUsedNote', '{{count}} clés n’ont jamais servi : révoquez celles qu’aucun système n’utilise.', { count: neverUsed })}
                    </p>
                )}
            </Card>

            <Card
                icon={Code2}
                iconTone="neutral"
                title={t('commerceOrders.docsTitle', 'Envoyer et lire vos commandes')}
                description={t('commerceOrders.docsIntro', 'Envoyez vos commandes par lots (jusqu’à 1 000 par appel). L’envoi est idempotent sur external_id : renvoyer une commande la met à jour, sans doublon.')}
            >
                <div className="co-v2__docs">
                    <DescriptionList
                        items={[
                            { label: t('commerceOrders.docsBase', 'Adresse'), value: <span className="u-code co-v2__wrap-code">{base}</span> },
                            { label: t('commerceOrders.docsAuth', 'Authentification'), value: <span className="u-code">X-API-Key: uo_…</span> },
                            { label: t('commerceOrders.docsLimits', 'Limites'), value: t('commerceOrders.docsLimitsValue', '1 000 commandes par envoi · 500 commandes par page en lecture · 120 appels par minute et par clé') },
                            { label: t('commerceOrders.docsStatuses', 'Statuts'), value: <span className="co-v2__mono">pending preparing shipped delivered incident returned cancelled</span> },
                        ]}
                    />
                    <Banner tone={monitoring ? 'success' : 'warning'}>
                        {monitoring
                            ? t('commerceOrders.docsMonitoringOn', 'Chaque numéro de suivi envoyé est surveillé par la surveillance colis : un problème vous est signalé sur cette page, par webhook et sur la commande Shopify / PrestaShop.')
                            : t('commerceOrders.docsMonitoringOff', 'La surveillance colis est désactivée : les commandes sont enregistrées mais leurs colis ne sont pas suivis. Activez-la dans Surveillance colis.')}
                    </Banner>
                    <CodeBlock label={t('commerceOrders.docsPush', 'Envoyer des commandes — POST /orders/batch')} code={pushExample(base)} />
                    <p className="u-help">{t('commerceOrders.docsParcelFields', 'Les modèles de risque lisent le transporteur, le client (client_ref), les codes postaux d’expédition (ship_from) et de livraison, l’assurance, la valeur de la commande et les dates : renseignez-les. Poids, dimensions, contenu, catégorie, mode de livraison et point relais servent au prochain entraînement des modèles.')}</p>
                    <p className="u-help">{t('commerceOrders.docsPushAnswer', 'Réponse : created, updated, errors (index + raison pour chaque commande refusée, les autres sont enregistrées), parcels_registered.')}</p>
                    <CodeBlock label={t('commerceOrders.docsPull', 'Lire les commandes — GET /orders (curseur)')} code={pullExample(base, t)} />
                    <p className="u-help">{t('commerceOrders.docsPullAnswer', 'Réponse : data (commandes avec l’état de chaque colis et les problèmes en cours), next_cursor, has_more. Une commande revient dès qu’elle change. Une seule commande : GET /orders/{external_id}?source=api|shopify|prestashop.')}</p>
                    <CodeBlock label={t('commerceOrders.docsShipment', 'Ajouter un colis expédié — POST /orders/{external_id}/shipments')} code={shipmentExample(base)} />
                    <p className="u-help">{t('commerceOrders.docsShipmentAnswer', 'Votre WMS a expédié la commande : le colis est ajouté et surveillé. Pour une commande Shopify ou PrestaShop dont la boutique a « numéros de suivi » activé, le numéro est inscrit sur la commande de la boutique (shop_push : queued). Plusieurs boutiques d’une même plateforme : ajoutez shop=<identifiant>.')}</p>
                </div>
            </Card>

            {confirmKey && (
                <ConfirmDialog
                    title={t('ordersMoneyUi.api.revokeTitle', 'Révoquer la clé « {{name}} » ?', { name: confirmKey.name })}
                    message={t('ordersMoneyUi.api.revokeMessage', 'Les systèmes qui l’utilisent ({{prefix}}…) ne pourront plus envoyer ni lire de commandes, dès maintenant. Cette action est irréversible.', { prefix: confirmKey.key_prefix })}
                    confirmLabel={t('commerceOrders.confirmRevoke', 'Révoquer définitivement')}
                    cancelLabel={t('commerceOrders.cancel', 'Annuler')}
                    onConfirm={revoke}
                    onCancel={() => setConfirmKey(null)}
                    busy={revoking}
                />
            )}
        </div>
    );
};

export default ApiTab;
